import React, { useState, useRef, useEffect } from 'react';
import { PARTICLES, HIGGS, CATEGORIES } from '../data';
import { COLLISIONS, findCollision } from '../collisionData';

const ALL_PARTICLES = [HIGGS, ...PARTICLES];

function catColor(p) {
  if (!p) return '#888';
  return CATEGORIES[p.category]?.color || '#888';
}

function AnimatedCollision({ p1, p2, result, running, onDone }) {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const stateRef = useRef({ phase: 'approach', t: 0 });

  useEffect(() => {
    if (!running) return;
    stateRef.current = { phase: 'approach', t: 0 };
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width, H = canvas.height;
    const cx = W / 2, cy = H / 2;
    const c1 = catColor(p1), c2 = catColor(p2);

    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      const s = stateRef.current;
      s.t += 0.018;

      if (s.phase === 'approach') {
        const dist = Math.max(0, 1 - s.t * 1.2);
        const x1 = cx - 80 * dist - 20;
        const x2 = cx + 80 * dist + 20;

        // Particle 1
        ctx.beginPath(); ctx.arc(x1, cy, 14, 0, Math.PI * 2);
        ctx.fillStyle = c1 + '40'; ctx.fill();
        ctx.beginPath(); ctx.arc(x1, cy, 9, 0, Math.PI * 2);
        ctx.fillStyle = c1; ctx.fill();
        ctx.font = 'bold 9px Space Mono, monospace';
        ctx.fillStyle = '#fff'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(p1?.symbol || '?', x1, cy);

        // Particle 2
        ctx.beginPath(); ctx.arc(x2, cy, 14, 0, Math.PI * 2);
        ctx.fillStyle = c2 + '40'; ctx.fill();
        ctx.beginPath(); ctx.arc(x2, cy, 9, 0, Math.PI * 2);
        ctx.fillStyle = c2; ctx.fill();
        ctx.fillStyle = '#fff';
        ctx.fillText(p2?.symbol || '?', x2, cy);

        // Motion lines
        for (let i = 0; i < 3; i++) {
          const ox = 25 + i * 10;
          ctx.beginPath();
          ctx.moveTo(x1 - ox, cy - 4 + i * 4);
          ctx.lineTo(x1 - ox + 15, cy - 4 + i * 4);
          ctx.strokeStyle = c1 + '60'; ctx.lineWidth = 1; ctx.stroke();
          ctx.beginPath();
          ctx.moveTo(x2 + ox, cy - 4 + i * 4);
          ctx.lineTo(x2 + ox - 15, cy - 4 + i * 4);
          ctx.strokeStyle = c2 + '60'; ctx.lineWidth = 1; ctx.stroke();
        }

        if (dist <= 0) { s.phase = 'impact'; s.t = 0; }

      } else if (s.phase === 'impact') {
        const r = s.t * 60;
        const alpha = Math.max(0, 1 - s.t * 1.5);
        // Explosion rings
        for (let i = 0; i < 3; i++) {
          ctx.beginPath();
          ctx.arc(cx, cy, r * (0.5 + i * 0.3), 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(255,220,80,${alpha * (1 - i * 0.25)})`;
          ctx.lineWidth = 2 - i * 0.5; ctx.stroke();
        }
        // Flash
        if (s.t < 0.3) {
          ctx.beginPath(); ctx.arc(cx, cy, 20, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(255,240,120,${(0.3 - s.t) * 3})`; ctx.fill();
        }
        if (s.t > 0.7) { s.phase = 'products'; s.t = 0; }

      } else if (s.phase === 'products') {
        const products = result?.products || [];
        const count = Math.min(products.length, 4);
        const angles = count === 1 ? [0] : count === 2 ? [-Math.PI / 4, Math.PI / 4] :
          count === 3 ? [-Math.PI / 3, 0, Math.PI / 3] :
          [-Math.PI / 2.5, -Math.PI / 8, Math.PI / 8, Math.PI / 2.5];

        const spread = Math.min(s.t * 80, 65);

        products.slice(0, count).forEach((prod, i) => {
          const angle = angles[i];
          const px = cx + Math.cos(angle) * spread;
          const py = cy + Math.sin(angle) * spread;
          const alpha = Math.min(1, s.t * 2);

          ctx.beginPath(); ctx.arc(px, py, 14, 0, Math.PI * 2);
          ctx.fillStyle = prod.color + '25'; ctx.fill();
          ctx.beginPath(); ctx.arc(px, py, 9, 0, Math.PI * 2);
          ctx.fillStyle = prod.color; ctx.globalAlpha = alpha; ctx.fill();
          ctx.globalAlpha = 1;

          // Trail
          ctx.beginPath();
          ctx.moveTo(cx, cy);
          ctx.lineTo(px, py);
          ctx.strokeStyle = prod.color + '30'; ctx.lineWidth = 1; ctx.stroke();

          ctx.font = '7px Space Mono, monospace';
          ctx.fillStyle = '#fff'; ctx.globalAlpha = alpha;
          ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
          ctx.fillText(prod.result.split(' ')[0], px, py);
          ctx.globalAlpha = 1;
        });

        if (s.t > 1.5) { s.phase = 'done'; onDone && onDone(); return; }
      }

      animRef.current = requestAnimationFrame(draw);
    };

    animRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animRef.current);
  }, [running, p1, p2, result]);

  return (
    <canvas ref={canvasRef} width={280} height={120}
      style={{ display: 'block', margin: '0 auto', width: '100%', maxWidth: '280px' }} />
  );
}

export default function CollisionSimulator({ mode }) {
  const isRuach = mode === 'ruach';
  const accentColor = isRuach ? '#ffb060' : '#f0c040';
  const [p1, setP1] = useState(null);
  const [p2, setP2] = useState(null);
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);

  const selectable = ALL_PARTICLES.filter(p =>
    ['electron', 'up', 'down', 'top', 'photon', 'w', 'higgs'].includes(p.id)
  );

  const handleCollide = () => {
    if (!p1 || !p2) return;
    const col = findCollision(p1.id, p2.id);
    setResult(col || null);
    setRunning(true);
    setDone(false);
  };

  const handleReset = () => {
    setP1(null); setP2(null); setResult(null);
    setRunning(false); setDone(false);
  };

  return (
    <div>
      <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.75rem', letterSpacing: '0.1em', marginBottom: '1.5rem', fontFamily: 'Space Mono', textTransform: 'uppercase' }}>
        {isRuach ? 'When forms meet — what emerges' : 'Select two particles and collide them'}
      </p>

      {/* Particle selector */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
        {[{ label: isRuach ? 'First form' : 'Particle 1', sel: p1, set: setP1 },
          { label: isRuach ? 'Second form' : 'Particle 2', sel: p2, set: setP2 }].map((slot, si) => (
          <div key={si}>
            <div style={{ fontSize: '0.65rem', fontFamily: 'Space Mono', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(255,255,255,0.3)', marginBottom: '0.6rem' }}>
              {slot.label}
            </div>
            {slot.sel ? (
              <div
                onClick={() => { slot.set(null); setResult(null); setRunning(false); setDone(false); }}
                style={{
                  padding: '0.75rem 1rem', borderRadius: '8px', cursor: 'pointer',
                  background: `${catColor(slot.sel)}18`,
                  border: `1px solid ${catColor(slot.sel)}50`,
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}
              >
                <div>
                  <div style={{ fontSize: '1rem', fontFamily: 'Space Mono', color: catColor(slot.sel), fontWeight: 700 }}>{slot.sel.symbol}</div>
                  <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.6)' }}>{slot.sel.name}</div>
                </div>
                <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.75rem' }}>×</span>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {selectable.map(p => (
                  <button key={p.id} onClick={() => { slot.set(p); setResult(null); setRunning(false); setDone(false); }} style={{
                    background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: '6px', padding: '6px 10px', cursor: 'pointer', color: 'inherit',
                    display: 'flex', alignItems: 'center', gap: '8px', textAlign: 'left',
                    transition: 'background 0.12s',
                  }}>
                    <span style={{ fontFamily: 'Space Mono', fontSize: '0.78rem', color: catColor(p), width: '24px' }}>{p.symbol}</span>
                    <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)' }}>{p.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Collide button */}
      {p1 && p2 && !running && (
        <button onClick={handleCollide} style={{
          width: '100%', padding: '0.875rem', borderRadius: '8px', cursor: 'pointer',
          background: `${accentColor}20`, border: `1px solid ${accentColor}50`,
          color: accentColor, fontFamily: 'Space Mono', fontSize: '0.8rem',
          letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 700,
          marginBottom: '1.25rem', transition: 'background 0.15s',
        }}>
          {isRuach ? `${p1.symbol} meets ${p2.symbol} — reveal` : `Collide ${p1.symbol} + ${p2.symbol}`}
        </button>
      )}

      {/* Animation */}
      {running && (
        <div style={{ marginBottom: '1.25rem', padding: '1rem', background: 'rgba(255,255,255,0.03)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.08)' }}>
          <AnimatedCollision p1={p1} p2={p2} result={result} running={running} onDone={() => setDone(true)} />
        </div>
      )}

      {/* Results */}
      {result && (running || done) && (
        <div style={{ marginBottom: '1.25rem' }}>
          <div style={{ fontSize: '0.65rem', fontFamily: 'Space Mono', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(255,255,255,0.25)', marginBottom: '0.75rem' }}>
            {isRuach ? 'What emerges' : 'Decay products'}
          </div>
          {result.products.map((prod, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: '0.75rem',
              padding: '0.6rem 0.75rem', borderRadius: '6px', marginBottom: '4px',
              background: `${prod.color}0d`, border: `1px solid ${prod.color}25`,
            }}>
              <div style={{ width: '36px', fontSize: '0.75rem', fontFamily: 'Space Mono', color: prod.color, fontWeight: 700, flexShrink: 0 }}>
                {prod.probability}%
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '0.82rem', color: prod.color, fontFamily: 'Space Mono', marginBottom: '2px' }}>{prod.result}</div>
                <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.45)' }}>{prod.description}</div>
              </div>
            </div>
          ))}

          <div style={{ marginTop: '1rem', padding: '0.875rem', borderRadius: '8px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
            <p style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.6)', margin: 0, lineHeight: 1.7 }}>
              {isRuach ? result.ruach : result.science}
            </p>
          </div>

          {/* Conservation laws */}
          <div style={{ marginTop: '0.75rem', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {result.conserved.map(c => (
              <span key={c} style={{ fontSize: '0.65rem', padding: '2px 8px', borderRadius: '20px', background: 'rgba(80,224,160,0.1)', border: '1px solid rgba(80,224,160,0.25)', color: '#50e0a0', fontFamily: 'Space Mono' }}>
                {c} ✓
              </span>
            ))}
          </div>

          <button onClick={handleReset} style={{
            marginTop: '1rem', padding: '0.6rem 1rem', borderRadius: '6px', cursor: 'pointer',
            background: 'none', border: '1px solid rgba(255,255,255,0.15)',
            color: 'rgba(255,255,255,0.5)', fontSize: '0.72rem', fontFamily: 'Space Mono',
          }}>
            Reset
          </button>
        </div>
      )}

      {p1 && p2 && !findCollision(p1.id, p2.id) && !running && (
        <div style={{ padding: '0.875rem', borderRadius: '8px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', fontSize: '0.82rem', color: 'rgba(255,255,255,0.4)', lineHeight: 1.7 }}>
          This combination doesn't have a defined interaction in our model yet. Try electron + positron, photon + photon, or top + anti-top for the most interesting results.
        </div>
      )}
    </div>
  );
}
